import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, Download, User, TrendingUp, Target, Brain, Users, Shield, Zap, Award, ChevronRight, BarChart3, MessageCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import AryanCareerBuddy, { AryanFloatingButton } from '../components/AryanCareerBuddy';
import AryanMentorV2 from '../components/AryanMentorV2';
import AryanUltraV3, { AryanUltraFloatingButton } from '../components/AryanUltraV3';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Chart colors
const COLORS = ['#CC5500', '#C5A065', '#4A90A4', '#50C878', '#9B59B6', '#E74C3C'];
const GRADIENT_COLORS = {
  primary: '#CC5500',
  secondary: '#C5A065',
  tertiary: '#4A90A4',
};

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1a1a1a] border border-white/10 rounded-lg px-4 py-3 shadow-xl">
        <p className="text-white font-medium">{label}</p>
        <p className="text-[#CC5500] font-bold">{payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { t, language } = useLanguage();
  const [searchParams] = useSearchParams();
  const [report, setReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAryan, setShowAryan] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    const reportId = searchParams.get('report_id');
    if (reportId) {
      fetchReport(reportId);
    } else {
      fetchReports();
    }
  }, [user, searchParams]);

  const fetchReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/report/${reportId}`);
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/reports`);
      setReports(response.data);
      if (response.data.length > 0) {
        setReport(response.data[0]);
      }
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleDownload = async (format) => {
    if (!report) return;
    try {
      const response = await axios.get(`${API}/report/download/${report.id}?format=${format}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `LeadEconomics_Report_${user.full_name.replace(' ', '_')}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Report downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center">
        <div className="text-center">
          <div className="relative w-16 h-16 mx-auto mb-4">
            <div className="absolute inset-0 border-4 border-[#CC5500]/20 rounded-full" />
            <div className="absolute inset-0 border-4 border-transparent border-t-[#CC5500] rounded-full animate-spin" />
          </div>
          <p className="text-gray-400">Loading your intelligence profile...</p>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-[#0D0D0D]" data-testid="no-reports-view">
        <nav className="border-b border-white/10 bg-[#0D0D0D]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <h1 className="text-2xl font-heading font-semibold text-white">
                Lead<span className="text-[#CC5500]">Economics</span>™
              </h1>
              <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </Button>
            </div>
          </div>
        </nav>
        <div className="max-w-4xl mx-auto px-4 py-24 text-center">
          <h2 className="text-4xl font-heading font-semibold text-white mb-6">No Reports Yet</h2>
          <p className="text-lg text-gray-400 mb-8">Complete your leadership assessment to receive your intelligence profile.</p>
          <Button onClick={() => navigate('/assessment')} className="bg-gradient-to-r from-[#CC5500] to-[#C5A065] text-white" data-testid="start-assessment-button">
            Begin Assessment
          </Button>
        </div>
        
        {/* Aryan Career Buddy - Available even without reports */}
        <AryanFloatingButton onClick={() => setShowAryan(true)} />
        <AryanMentorV2 
          isOpen={showAryan} 
          onClose={() => setShowAryan(false)} 
          token={token}
          isProfessional={user?.user_type === 'professional'}
        />
      </div>
    );
  }

  const insights = report.insights || {};

  // Prepare chart data
  const leadershipMatrix = [
    { name: 'Impact', score: insights.leadership_evaluation_matrix?.impact || 78, fullMark: 100 },
    { name: 'Fit', score: insights.leadership_evaluation_matrix?.fit || 82, fullMark: 100 },
    { name: 'Agility', score: insights.leadership_evaluation_matrix?.agility || 75, fullMark: 100 },
    { name: 'Risk', score: insights.leadership_evaluation_matrix?.risk || 68, fullMark: 100 },
  ];

  const cognitiveData = [
    { dimension: 'Strategic Clarity', value: insights.cognitive_assessment?.strategic_clarity || 85 },
    { dimension: 'Foresight', value: insights.cognitive_assessment?.strategic_foresight || 78 },
    { dimension: 'Decision Holistics', value: insights.cognitive_assessment?.decision_holistics || 72 },
    { dimension: 'Cognitive Depth', value: insights.cognitive_assessment?.cognitive_depth || 88 },
    { dimension: 'Adaptive Agility', value: insights.cognitive_assessment?.adaptive_agility || 75 },
    { dimension: 'Risk Orientation', value: insights.cognitive_assessment?.risk_orientation || 65 },
  ];

  const executionData = [
    { metric: 'Initiative', value: insights.execution_intensity_index?.initiative || 82 },
    { metric: 'Ownership', value: insights.execution_intensity_index?.ownership || 78 },
    { metric: 'Cadence', value: insights.execution_intensity_index?.cadence || 85 },
    { metric: 'Resilience', value: insights.execution_intensity_index?.resilience || 72 },
  ];

  const competencyRadar = [
    { subject: 'Strategic', A: 85, fullMark: 100 },
    { subject: 'Execution', A: 78, fullMark: 100 },
    { subject: 'People', A: 72, fullMark: 100 },
    { subject: 'Innovation', A: 80, fullMark: 100 },
    { subject: 'Composure', A: 75, fullMark: 100 },
    { subject: 'Influence', A: 82, fullMark: 100 },
  ];

  const compatibilityData = [
    { name: 'Company Culture', value: 85 },
    { name: 'Team Dynamics', value: 78 },
    { name: 'Boss Style', value: 72 },
    { name: 'Role Requirements', value: 88 },
  ];

  const strengthsData = [
    { name: 'Strategic Thinking', value: insights.leadership_strengths?.strategic_thinking || 88 },
    { name: 'Decision Making', value: insights.leadership_strengths?.decision_making || 82 },
    { name: 'Execution', value: insights.leadership_strengths?.adaptability || 78 },
    { name: 'Communication', value: insights.leadership_strengths?.effective_communication || 75 },
    { name: 'Innovation', value: insights.leadership_strengths?.innovation || 80 },
  ];

  const overallScore = insights.leadership_evaluation_matrix?.overall || 78;

  return (
    <div className="min-h-screen bg-[#0D0D0D]" data-testid="dashboard-page">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#0D0D0D]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-2xl font-heading font-semibold text-white" data-testid="dashboard-title">
              Lead<span className="text-[#CC5500]">Economics</span>™
            </h1>
            <div className="flex items-center gap-4">
              <LanguageSelector variant="minimal" />
              <div className="hidden sm:flex items-center gap-2 text-sm text-gray-400">
                <User className="h-4 w-4" />
                {user?.full_name}
              </div>
              <Button onClick={handleLogout} variant="outline" className="border-white/20 text-white hover:bg-white/10" data-testid="logout-button">
                <LogOut className="mr-2 h-4 w-4" />
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Score */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Leadership Intelligence Profile</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-bold text-white mb-2">
                {user?.full_name}
              </h2>
              <p className="text-xs text-gray-500 font-mono">Report ID: {report.id?.slice(0, 8).toUpperCase()}</p>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Overall Score Ring */}
              <div className="relative w-24 h-24">
                <svg className="w-24 h-24 transform -rotate-90">
                  <circle cx="48" cy="48" r="40" fill="none" stroke="#1a1a1a" strokeWidth="8" />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    fill="none"
                    stroke="url(#scoreGradient)"
                    strokeWidth="8"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - overallScore / 100)}`}
                    strokeLinecap="round"
                  />
                  <defs>
                    <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#CC5500" />
                      <stop offset="100%" stopColor="#C5A065" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-bold text-white">{overallScore}</span>
                  <span className="text-xs text-gray-500">Score</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => handleDownload('pdf')}
                  variant="outline"
                  className="border-[#CC5500]/50 text-[#CC5500] hover:bg-[#CC5500]/10"
                  data-testid="download-pdf-button"
                >
                  <Download className="mr-2 h-4 w-4" />
                  PDF
                </Button>
                <Button
                  onClick={() => handleDownload('word')}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="download-word-button"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Word
                </Button>
              </div>
            </div>
          </div>

          {/* Executive Summary Card */}
          <div className="bg-gradient-to-br from-white/5 to-transparent border border-white/10 rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
              <Award className="h-5 w-5 text-[#C5A065]" />
              Executive Summary
            </h3>
            <p className="text-gray-400 leading-relaxed">
              {insights.executive_summary || 
                `${user?.full_name} demonstrates a balanced leadership profile with strong analytical capabilities and measured risk tolerance. 
                The assessment reveals high execution intensity paired with collaborative stakeholder engagement. 
                Decision-making patterns indicate a preference for validated, data-driven approaches while maintaining adaptability under pressure.`}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="bg-white/5 border border-white/10 p-1 rounded-xl">
            <TabsTrigger value="overview" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="cognitive" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg" data-testid="tab-cognitive">Cognitive</TabsTrigger>
            <TabsTrigger value="behavioral" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg" data-testid="tab-behavioral">Behavioral</TabsTrigger>
            <TabsTrigger value="compatibility" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg" data-testid="tab-compatibility">Compatibility</TabsTrigger>
            <TabsTrigger value="roadmap" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#CC5500] data-[state=active]:to-[#C5A065] data-[state=active]:text-white rounded-lg" data-testid="tab-roadmap">Roadmap</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leadership Matrix */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#CC5500]" />
                  Leadership Evaluation Matrix
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={leadershipMatrix} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} stroke="#666" />
                    <YAxis dataKey="name" type="category" stroke="#666" width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" fill="url(#barGradient)" radius={[0, 8, 8, 0]} barSize={30}>
                      <defs>
                        <linearGradient id="barGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                          <stop offset="0%" stopColor="#CC5500" />
                          <stop offset="100%" stopColor="#C5A065" />
                        </linearGradient>
                      </defs>
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Competency Radar */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-[#CC5500]" />
                  Competency Radar
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={competencyRadar}>
                    <PolarGrid stroke="#333" />
                    <PolarAngleAxis dataKey="subject" stroke="#666" tick={{ fill: '#888', fontSize: 12 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#333" tick={{ fill: '#666' }} />
                    <Radar name="Profile" dataKey="A" stroke="#CC5500" fill="#CC5500" fillOpacity={0.3} strokeWidth={2} />
                    <Tooltip content={<CustomTooltip />} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Strengths Bar Chart */}
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#CC5500]" />
                Leadership Strengths
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={strengthsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#666" tick={{ fill: '#888', fontSize: 11 }} />
                  <YAxis domain={[0, 100]} stroke="#666" />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="value" fill="url(#strengthGradient)" radius={[8, 8, 0, 0]} barSize={50}>
                    <defs>
                      <linearGradient id="strengthGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#CC5500" />
                        <stop offset="100%" stopColor="#C5A065" />
                      </linearGradient>
                    </defs>
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          {/* Cognitive Tab */}
          <TabsContent value="cognitive" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cognitive Dimensions */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Brain className="h-5 w-5 text-[#CC5500]" />
                  Cognitive Dimensions
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={cognitiveData}>
                    <defs>
                      <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#CC5500" stopOpacity={0.4}/>
                        <stop offset="95%" stopColor="#CC5500" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="dimension" stroke="#666" tick={{ fill: '#888', fontSize: 10 }} angle={-45} textAnchor="end" height={80} />
                    <YAxis domain={[0, 100]} stroke="#666" />
                    <Tooltip content={<CustomTooltip />} />
                    <Area type="monotone" dataKey="value" stroke="#CC5500" fillOpacity={1} fill="url(#areaGradient)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Decision Style */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-[#CC5500]" />
                  Decision Architecture Index
                </h3>
                <div className="space-y-4">
                  {[
                    { label: 'Decision Velocity', value: insights.decision_architecture_index?.decision_velocity || 78 },
                    { label: 'Validation Discipline', value: insights.decision_architecture_index?.validation_discipline || 82 },
                    { label: 'Reversibility Judgment', value: insights.decision_architecture_index?.reversibility_judgement || 72 },
                    { label: 'Stakeholder Inclusion', value: insights.decision_architecture_index?.inclusion_behavior || 85 },
                  ].map((item, index) => (
                    <div key={index}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-400">{item.label}</span>
                        <span className="text-white font-semibold">{item.value}%</span>
                      </div>
                      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-[#CC5500] to-[#C5A065] rounded-full transition-all duration-1000"
                          style={{ width: `${item.value}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-[#CC5500]/10 rounded-xl border border-[#CC5500]/20">
                  <p className="text-sm text-gray-400">
                    <strong className="text-white">DAI Score:</strong> {insights.decision_architecture_index?.dai_score || 79}% - {insights.decision_architecture_index?.rating || 'Strong'}
                  </p>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Behavioral Tab */}
          <TabsContent value="behavioral" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Execution Intensity */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-[#CC5500]" />
                  Execution Intensity Index
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={executionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ metric, value }) => `${metric}: ${value}%`}
                      labelLine={false}
                    >
                      {executionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-4 mt-4">
                  {executionData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                      <span className="text-xs text-gray-400">{item.metric}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Behavior Under Pressure */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#CC5500]" />
                  Behavior Under Pressure
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <h4 className="text-white font-medium mb-2">Stress Response Pattern</h4>
                    <p className="text-sm text-gray-400">
                      {insights.pressure_response_patterns || 
                        'Under high-pressure conditions, maintains composure and shifts into structured problem-solving mode. Stress response characterized by increased focus on data and systematic approaches.'}
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <h4 className="text-white font-medium mb-2">Resilience Factors</h4>
                    <p className="text-sm text-gray-400">
                      {insights.resilience_factors || 
                        'Strong emotional regulation capabilities with ability to compartmentalize stress while maintaining strategic perspective. Recovery time from setbacks is moderate.'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Compatibility Tab */}
          <TabsContent value="compatibility" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Compatibility Chart */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Users className="h-5 w-5 text-[#CC5500]" />
                  Compatibility Analysis
                </h3>
                <ResponsiveContainer width="100%" height={280}>
                  <RadarChart data={compatibilityData.map(d => ({ subject: d.name, A: d.value, fullMark: 100 }))}>
                    <PolarGrid stroke="#333" />
                    <PolarAngleAxis dataKey="subject" stroke="#666" tick={{ fill: '#888', fontSize: 11 }} />
                    <PolarRadiusAxis angle={30} domain={[0, 100]} stroke="#333" />
                    <Radar name="Compatibility" dataKey="A" stroke="#C5A065" fill="#C5A065" fillOpacity={0.3} strokeWidth={2} />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              {/* Role Suitability */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                  <Target className="h-5 w-5 text-[#CC5500]" />
                  Role Suitability
                </h3>
                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-r from-[#CC5500]/10 to-transparent rounded-xl border border-[#CC5500]/20">
                    <h4 className="text-[#CC5500] font-medium mb-2">Best-Fit Roles</h4>
                    <ul className="space-y-2">
                      {(insights.best_fit_contexts || [
                        'Strategic operations leadership in growth-stage organizations',
                        'CEO/COO roles requiring balance of vision and execution',
                        'Turnaround leadership in structured environments',
                      ]).map((role, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                          <ChevronRight className="h-4 w-4 text-[#CC5500] mt-0.5 flex-shrink-0" />
                          {role}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Speed', value: 'Moderate-Fast' },
                      { label: 'Risk', value: 'Measured' },
                      { label: 'Culture', value: 'Meritocratic' },
                    ].map((item, i) => (
                      <div key={i} className="text-center p-3 bg-white/5 rounded-xl">
                        <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                        <p className="text-sm text-white font-medium">{item.value}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Roadmap Tab */}
          <TabsContent value="roadmap" className="space-y-6">
            <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#CC5500]" />
                90/180/365 Day Integration Roadmap
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    period: '90 Days',
                    color: '#CC5500',
                    items: insights.development_plan?.first_90_days || [
                      'Establish stakeholder trust through transparent communication',
                      'Build decision-making frameworks aligned with organizational rhythm',
                      'Identify 2-3 quick wins demonstrating execution capability',
                    ],
                  },
                  {
                    period: '180 Days',
                    color: '#C5A065',
                    items: insights.development_plan?.['90_180_days'] || [
                      'Solidify team structure and delegation architecture',
                      'Implement strategic initiatives with measurable KPIs',
                      'Refine leadership style based on organizational feedback',
                    ],
                  },
                  {
                    period: '365 Days',
                    color: '#4A90A4',
                    items: insights.development_plan?.['180_365_days'] || [
                      'Establish long-term strategic vision and execution rhythm',
                      'Build succession planning and leadership development pipeline',
                      'Optimize cultural alignment and organizational effectiveness',
                    ],
                  },
                ].map((phase, index) => (
                  <div key={index} className="relative">
                    <div 
                      className="absolute left-0 top-0 bottom-0 w-1 rounded-full"
                      style={{ backgroundColor: phase.color }}
                    />
                    <div className="pl-6">
                      <h4 className="text-lg font-semibold text-white mb-4" style={{ color: phase.color }}>
                        {phase.period}
                      </h4>
                      <ul className="space-y-3">
                        {phase.items.map((item, i) => (
                          <li key={i} className="text-sm text-gray-400 flex items-start gap-2">
                            <div className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: phase.color }} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Aryan Ultra V3 - Multi-Model AI Powerhouse (Claude + GPT + Gemini) */}
      <>
        <AryanUltraFloatingButton 
          onClick={() => setShowAryan(true)} 
          isInternational={user?.user_type === 'international_student' || user?.is_international}
        />
        <AryanUltraV3 
          isOpen={showAryan} 
          onClose={() => setShowAryan(false)} 
          token={token}
          isInternational={user?.user_type === 'international_student' || user?.is_international}
        />
      </>
    </div>
  );
};
